import React from 'react';
import URL from 'url-parse';
import Head from 'next/head';
import get from 'lodash/get';
import getConfig from 'next/config';

import Layout from '~/src/components/layout/Layout';
import ArticleContent from '~/src/components/content/article/ArticleContent';
import TickerContent from '~/src/components/content/ticker/TickerContent';
import SlideshowContent from '~/src/components/content/slideshow/SlideshowContent';
import VideoContent from '~/src/components/content/video/VideoContent';
import Ad from '~/src/components/ad/Ad';
import CommentArea from '~/src/components/comment/area/CommentArea';
import Breadcrumb from '~/src/components/breadcrumb/Breadcrumb';
import Outbrain from '~/src/components/outbrain/Outbrain';
import TeaserSection from '~/src/components/teaser/section/TeaserSection';
import EmbedContent from '~/src/components/content/embed/EmbedContent';
import StructuredBreadcrumbsContent from '~/src/components/structured/breadcrumbs/StructuredBreadcrumbsContent';
import ReactionProvider from '~/src/components/context/ReactionContext';

import {getFeedUrl} from '~/src/utilities/helpers/feed';
import {getJsonRequest} from '~/src/utilities/helpers/request';
import {parseContent, parseRelatedStories, parseRelatedVideos, parseSeriesVideos} from '~/src/utilities/feed/data';
import {applyQuery, getContentUrl, getVideoUrl} from '~/src/utilities/helpers/link';
import {loadBeagleDependency, daktIsReady, trackRouteChange} from '~/src/utilities/tracking/beagle';
import {updateTotmOnClient} from '~/src/utilities/libs/totm';
import {SiteArea, SiteAreaName} from '~/src/utilities/constants/navigation';
import {trackGtm, buildNavStatusLevels} from '~/src/utilities/tracking/gtm';
import {sendPixel, mapContentType, updateChannel} from '~/src/utilities/tracking/netmetrix';
import {getComments} from '~/src/utilities/feed/comment';
import {setCacheControl} from '~/src/utilities/helpers/header';
import {isEnabled} from '~/src/utilities/helpers/feature';
import {Feature} from '~/src/utilities/constants/feature';
import {ContentType} from '~/src/utilities/constants/types';
import {GtmEvents, GtmPageTypes} from '~/src/utilities/constants/tracking';
import {ResponseError} from '~/src/utilities/types/error';
import {showContentAd, getAdUnitName} from '~/src/utilities/helpers/ad';
import {hasAmp, getAmpUrl} from '~/src/utilities/helpers/amp';
import {AdFormat, TeaserFormat} from '~/src/utilities/constants/formats';
import {withSettingsContext} from '~/src/components/context/SettingsContext';
import {withSitemapContext} from '~/src/components/context/SitemapContext';
import {pushRoute} from '~/src/utilities/helpers/router';
import {SectionLayout} from '~/src/utilities/constants/layout';
import {getCategoryBy, getCategoryList} from '~/src/utilities/feed/sitemap';
import initPolyglot from '~/src/utilities/libs/polyglot';
import {createFormat} from '~/src/utilities/feed/format';
import {classNames} from '~/src/utilities/helpers/css';
import {getMetaTeaser} from '~/src/utilities/helpers/metaTeaser';
import TwitterTag from '../../components/metatag/twitter/twitterTag';
import OgTag from '../../components/metatag/facebook/ogTag';
import {IS_HTTP_PROTOCOL} from '~/src/utilities/constants/regex';
import PageContext from '~/src/components/context/PageContext';
import {Logger} from '~/src/utilities/helpers/log';

import css from './ContentPage.module.scss';
import i18n from './ContentPage.i18n.json';

const getTranslation = initPolyglot(i18n);

const {serverRuntimeConfig, publicRuntimeConfig: config} = getConfig();

class ContentPage extends React.Component {
    static async getInitialProps({query, asPath, res}, {sitemap}) {
        try {
            let data;

            if (!process.browser && query[serverRuntimeConfig.preview.queryKey]) {
                try {
                    data = await getJsonRequest(
                        getFeedUrl('preview', {
                            metadataId: query.id,
                            previewKey: query[serverRuntimeConfig.preview.queryKey]
                        })
                    );
                } catch (error) {
                    Logger.info(`[${error.name}][ContentPage] Error while loading preview feed`, {error});

                    return {
                        error
                    };
                }
            } else {
                try {
                    data = await getJsonRequest(getFeedUrl('content', {metadataId: query.id}));
                } catch (error) {
                    Logger.info(`[${error.name}][ContentPage] Error while loading content feed`, {error});

                    return {
                        error
                    };
                }

                const url = get(data, 'content.meta.url');
                const contentUrl = data.type === ContentType.VIDEO ? getVideoUrl(url) : getContentUrl(url);
                if (asPath.indexOf(contentUrl) !== 0) {
                    if (process.browser) {
                        pushRoute(sitemap, applyQuery(asPath, contentUrl));
                    } else {
                        setCacheControl(res, 900, get(data, 'content.meta.updated'));
                        res.redirect(301, applyQuery(asPath, contentUrl));
                    }
                    return {};
                }

                if (data.type === ContentType.VIDEO && !isEnabled(Feature.VIDEO)) {
                    const statusCode = 404;
                    const statusText = 'Page not found';
                    return {
                        error: new ResponseError({statusCode, statusText})
                    };
                }
            }

            if (data.type === ContentType.LINK) {
                const redirectLink = get(data, 'content.link.url');

                if (IS_HTTP_PROTOCOL.test(redirectLink)) {
                    setCacheControl(res, 900, get(data, 'content.meta.updated'));
                    res.redirect(301, applyQuery(asPath, redirectLink));
                    return {};
                }

                const statusCode = 410;
                const statusText = 'No longer available';
                return {
                    error: new ResponseError({statusCode, statusText})
                };
            }

            if (data.type === ContentType.ARTICLE || data.type === ContentType.BREAKINGNEWS) {
                data = parseContent(data, {sitemap});
            }

            const pageItem = getCategoryBy(getCategoryList(sitemap), {fullUrlPath: asPath});
            const isVideoPageStyle = pageItem && pageItem.pageStyle === 'video';

            const siteArea = (() => {
                if (isVideoPageStyle || data.type === ContentType.VIDEO) {
                    return SiteArea.VIDEO;
                }
                if (data.type === ContentType.LIFESTYLE) {
                    return SiteArea.LIFESTYLE;
                }
                return SiteArea.NEWS;
            })();

            const category = get(data, 'content.meta.mainCategory');
            const navigation = {
                categoryId: category,
                isContentPage: true
            };

            if (data.type === ContentType.VIDEO) {
                if (get(data, 'content.meta.series.id')) {
                    const seriesFeedUrl = getFeedUrl('category', {categoryId: get(data, 'content.meta.series.id')});
                    const seriesResponse = await getJsonRequest(seriesFeedUrl);
                    data.series = parseSeriesVideos(seriesResponse, siteArea);
                    data.seriesName = get(data, 'content.meta.series.name');
                }
            }

            if (isEnabled(Feature.COMMENTS) && get(data, 'content.meta.settings.commentingEnabled', false)) {
                try {
                    if (process.browser || !query[serverRuntimeConfig.preview.queryKey]) {
                        data.commenting = await getComments(query.id, {limit: 3});
                        data.commenting.total = Number(get(data, 'content.meta.clientdata.community.comments', '0'));
                    }
                } catch (error) {
                    Logger.info(`[${error.name}][ContentPage] Error while loading comments`, {error});

                    data.content.meta.settings.commentingEnabled = false;
                }
            }

            data.id = query.id;

            if (serverRuntimeConfig.pageCaching) {
                setCacheControl(res, 30, get(data, 'content.meta.updated'));
            }

            return {
                data,
                navigation,
                siteArea
            };
        } catch (error) {
            Logger.error(`[${error.name}][ContentPage] Error while loading content page data`, {error});

            return {
                error
            };
        }
    }

    state = {relatedContent: null};

    getTeaser(teaser) {
        return this.props.content.meta.teaser[teaser] || this.props.content.meta.teaser['default'];
    }

    getContentPage(teasers) {
        switch (this.props.type) {
            case ContentType.ARTICLE:
            case ContentType.BREAKINGNEWS:
            case ContentType.LIFESTYLE:
                return <ArticleContent {...this.props} teasers={teasers} />;
            case ContentType.TICKER:
                return <TickerContent {...this.props} teasers={teasers} />;
            case ContentType.SLIDESHOW:
                return <SlideshowContent {...this.props} />;
            case ContentType.VIDEO:
                return <VideoContent {...this.props} />;
            case ContentType.EMBED:
                return <EmbedContent {...this.props} />;
            default:
                return <div></div>;
        }
    }

    getGtmPageType() {
        switch (this.props.type) {
            case ContentType.ARTICLE:
            case ContentType.BREAKINGNEWS:
            case ContentType.LIFESTYLE:
                return GtmPageTypes.ARTICLE;
            case ContentType.TICKER:
                return GtmPageTypes.NEWSTICKER;
            case ContentType.SLIDESHOW:
                return GtmPageTypes.SLIDESHOW;
            default:
                return GtmPageTypes.OTHER;
        }
    }

    async loadRelatedContent(data) {
        let relatedContent;

        if (data.type === ContentType.VIDEO) {
            const categoryFeedUrl = getFeedUrl('category', {categoryId: get(data, 'content.meta.mainCategory')});
            const relatedResponse = await getJsonRequest(categoryFeedUrl);

            relatedContent = parseRelatedVideos(relatedResponse, data.context.settings.siteArea);
        } else {
            relatedContent = parseRelatedStories(data);
        }

        if (get(relatedContent, 'elements.length', 0) > 0) {
            relatedContent.elements = relatedContent.elements.map((element) => ({...element, format: createFormat(TeaserFormat.TEASER_SMALL)}));
            return relatedContent;
        }

        return {relatedContent: null};
    }

    async loadRecommendedContent(id) {
        try {
            await loadBeagleDependency();
            await daktIsReady();

            const recommenderEndpoint = this.props.type === ContentType.VIDEO ? 'relatedVideos' : 'relatedStories';

            const fullRecommenderUrl = `${config.scripts.recommender}/${recommenderEndpoint}?tenantid=${
                config.tenantId
            }&storyid=${id}&userid=${window.Dakt2.getProfId()}`;

            const dataFromRecommender = await getJsonRequest(fullRecommenderUrl);

            if (dataFromRecommender.content.elements.length) {
                dataFromRecommender.content.elements.forEach((item, index) => (item.sortID = index));

                return {
                    relatedContent: {
                        showTitle: true,
                        layout: SectionLayout.RELATED_CONTENT,
                        elements: dataFromRecommender.content.elements.map((element) => ({...element, format: createFormat(TeaserFormat.TEASER_SMALL)})),
                        trackingData: dataFromRecommender.trackingData
                    }
                };
            }

            return this.loadRelatedContent(this.props);
        } catch (error) {
            Logger.info(`[${error.name}][ContentPage] Error while loading recommendations`, {error});
            return this.loadRelatedContent(this.props);
        }
    }

    constructor(props) {
        super(props);

        const meta = get(props, 'content.meta', {});
        const adsEnabled = get(meta, 'settings.adsEnabled', true);
        const channel = meta.mainCategoryFullUrlPath;

        updateTotmOnClient({
            adsEnabled: adsEnabled,
            adChannel: channel,
            pagetype: props.type === ContentType.VIDEO ? 'videostory' : 'story',
            storyid: props.id,
            isWebview: props.context.settings.isWebview
        });
        updateChannel(channel);
    }

    componentDidMount() {
        const {settings, sitemap} = this.props.context;
        const categoryId = get(this, 'props.content.meta.mainCategory');
        trackRouteChange();
        trackGtm(GtmEvents.PAGEVIEW, {
            pageType: this.getGtmPageType(),
            data: {
                ...this.props,
                ...buildNavStatusLevels(SiteAreaName[settings.siteArea], categoryId, sitemap.all)
            }
        });
        sendPixel(mapContentType(this.props.type));

        if (this.props.type !== ContentType.EMBED) {
            if (
                (this.props.type === ContentType.VIDEO && isEnabled(Feature.RECOMMENDER_VIDEO)) ||
                (this.props.type !== ContentType.VIDEO && isEnabled(Feature.RECOMMENDER_STORY))
            ) {
                this.loadRecommendedContent(this.props.id).then(({relatedContent}) => this.setState({relatedContent}));
            } else {
                this.loadRelatedContent(this.props).then((relatedContent) => this.setState({relatedContent}));
            }
        }
    }

    render() {
        const teaserObject = getMetaTeaser(this.props);
        const meta = get(this.props, 'content.meta', {});
        const canonicalUrl = new URL(getContentUrl(meta.url), config.canonicalBaseUrl).href;
        const showComments = isEnabled(Feature.COMMENTS) && get(meta, 'settings.commentingEnabled', false);
        const showBreadcrumbs = this.props.type !== ContentType.EMBED;
        const allowRobots = get(meta, 'settings.allowRobots', false);
        const {siteArea} = this.props.context.settings;

        const showBrandday = showContentAd(this.props.type, AdFormat.INSIDE_FULL_BRANDDAY);
        const showFullTop = showContentAd(this.props.type, AdFormat.INSIDE_FULL_TOP);
        const showOutsideRight = showContentAd(this.props.type, AdFormat.OUTSIDE_RIGHT);
        const showContentEnd = showContentAd(this.props.type, AdFormat.CONTENT_END);
        const showAmpLink = hasAmp(this.props);
        const contentLayout = [ContentType.ARTICLE, ContentType.LIFESTYLE].includes(this.props.type);
        const title = getTranslation(teaserObject.web.titleHeader ? 'titleWithTitleHeader' : 'title', {
            name: config.tenantName,
            title: teaserObject.web.title,
            titleHeader: teaserObject.web.titleHeader
        });
        const titleBreadcrumb = getTranslation(teaserObject.web.titleHeader ? 'titleWithTitleHeaderBreadcrumb' : 'titleBreadcrumb', teaserObject.web);

        const pageClass = classNames(css, 'contentPage', siteArea);
        const showRelatedContent = this.state.relatedContent && siteArea !== SiteArea.LIFESTYLE;
        const pageContext = {canonicalUrl, gtmPageType: this.getGtmPageType()};

        return (
            <PageContext.Provider value={pageContext}>
                <ReactionProvider enabled={showComments}>
                    <StructuredBreadcrumbsContent url={meta.url} name={titleBreadcrumb} type={this.props.type} mainCategory={meta.mainCategory} />
                    <Head>
                        {/* general */}
                        <title>{title}</title>
                        {!allowRobots && <meta key="robots" name="robots" content="noindex, follow" />}
                        <meta key="title" name="title" content={teaserObject.web.title} />
                        <meta key="description" name="description" content={teaserObject.web.description} />
                        {allowRobots && <link key="canonical" rel="canonical" href={canonicalUrl} />}
                        {/* Coral */}
                        <meta key="coralPublishedTime" property="article:published_time" content={get(meta, 'published', '')} />
                        {/* AMP */}
                        {showAmpLink && <link key="ampHtml" rel="amphtml" href={getAmpUrl(this.props)} />}
                    </Head>
                    {/* open graph */}
                    <OgTag facebookTeaser={teaserObject.facebook} siteArea={siteArea} canonicalUrl={canonicalUrl} meta={meta} />
                    {/* Twitter */}
                    <TwitterTag twitterTeaser={teaserObject.twitter} />

                    <Layout contentType={contentLayout}>
                        <div className={pageClass}>
                            {showBrandday && (
                                <Ad
                                    className={css.insideFullBrandday}
                                    adUnitName={getAdUnitName('inside-full-brandday')}
                                    format={AdFormat.INSIDE_FULL_BRANDDAY}
                                />
                            )}
                            {showFullTop && (
                                <Ad className={css.insideFullTop} adUnitName={getAdUnitName('inside-full-top')} format={AdFormat.INSIDE_FULL_TOP} />
                            )}
                            {showOutsideRight && (
                                <div className={css.outsideRight}>
                                    <Ad adUnitName={getAdUnitName('outside-right')} format={AdFormat.OUTSIDE_RIGHT} />
                                </div>
                            )}

                            {this.getContentPage(teaserObject)}
                            {showComments && <CommentArea {...this.props.commenting} id={this.props.id} page="ContentPage" />}
                            {showContentEnd && <Ad adUnitName="inside-full-content-end" />}

                            {showBreadcrumbs && <Breadcrumb mainCategoryId={meta.mainCategory} title={titleBreadcrumb} />}

                            <Outbrain contentType={this.props.type} canonicalUrl={canonicalUrl} contextKey={meta.mainCategoryFullUrlPath} />

                            {showRelatedContent && <TeaserSection {...this.state.relatedContent} />}
                        </div>
                    </Layout>
                </ReactionProvider>
            </PageContext.Provider>
        );
    }
}

export default withSettingsContext(withSitemapContext(ContentPage));
